import { infiniteQueryOptions, queryOptions, useInfiniteQuery } from "@tanstack/react-query";
import api from "~/shared/api/core/api";
import { InterviewAlignmentCategory, InterviewRoundType } from "~/shared/api/interviews/interviews.types";
import { Params } from "~/shared/api/types/params";
import { client } from "~/shared/lib/client";
import { INote } from "~/shared/types/note.types";
import { PaginatedResponse } from "~/shared/types/pagination.types";
import { ensureApiData } from "~/shared/util/api.helpers";
import { createInfinitePaginationConfig } from "~/shared/util/createInfinitePaginationConfig";

export const INTERVIEWS_KEYS = {
  all: ["interviews"] as const,
  detail: (interviewId: string | null) => {
    return [...INTERVIEWS_KEYS.all, interviewId] as const;
  },
  list: (params: Params) => {
    return [...INTERVIEWS_KEYS.all, params] as const;
  },
  alignmentScores: (interviewId: string) => {
    return [...INTERVIEWS_KEYS.detail(interviewId), "alignmentScores"] as const;
  },
  alignmentNotes: (interviewId: string) => {
    return [...INTERVIEWS_KEYS.detail(interviewId), "alignmentNotes"] as const;
  },
  alignmentNotesList: (interviewId: string, params: Params) => {
    return [...INTERVIEWS_KEYS.alignmentNotes(interviewId), params] as const;
  },
  roundTypes: (params: Params) => {
    return [...INTERVIEWS_KEYS.all, "roundTypes", params] as const;
  },
  closingNotes: (interviewId: string | null) => {
    return [...INTERVIEWS_KEYS.detail(interviewId), "closingNotes"] as const;
  },
  listGlobalInterviewPhases: () => {
    return [...INTERVIEWS_KEYS.all, "globalInterviewPhases"] as const;
  },
  globalInterviewPhaseById: (id: string) => {
    return [...INTERVIEWS_KEYS.listGlobalInterviewPhases(), id] as const;
  },
  interviewRoundTypes: () => {
    return [...INTERVIEWS_KEYS.all, "interviewRoundTypes"] as const;
  },
  interviewRoundTypeById: (interviewRoundTypeId: string) => {
    return [...INTERVIEWS_KEYS.interviewRoundTypes(), interviewRoundTypeId] as const;
  },
  member: {
    all: () => [...INTERVIEWS_KEYS.all, "member"] as const,
    detail: (interviewId: string) => {
      return [...INTERVIEWS_KEYS.member.all(), interviewId] as const;
    },
  },
} as const;

export const INTERVIEWS_QUERIES = {
  detail: (id: string | null) => {
    return queryOptions({
      queryKey: INTERVIEWS_KEYS.detail(id),
      queryFn: async ({ signal }) => {
        if (id == null) {
          return null;
        }
        const response = await api.get(`/interviews/${id}`, {
          signal,
        });

        return response.data;
      },
    });
  },
  list: (params: Params) => {
    return queryOptions({
      queryKey: INTERVIEWS_KEYS.list(params),
      queryFn: async ({ signal }) => {
        const response = await api.get("/interviews", {
          ...params,
          signal,
        });

        return response.data;
      },
    });
  },
  alignmentScoresList: (interviewId: string) => {
    return queryOptions({
      queryKey: INTERVIEWS_KEYS.alignmentScores(interviewId),
      queryFn: async ({ signal }) => {
        const response = await api.get<PaginatedResponse<InterviewAlignmentCategory>>(
          `/interviews/${interviewId}/alignment-scores`,
          {
            params: {
              skip: 0,
              limit: 100,
            },
            signal,
          },
        );

        return response.data;
      },
    });
  },
  alignmentNotesList: (
    interviewId: string,
    params: Params = {
      params: {
        skip: 0,
        limit: 100,
      },
    },
  ) => {
    return queryOptions({
      queryKey: INTERVIEWS_KEYS.alignmentNotesList(interviewId, params),
      queryFn: async ({ signal }) => {
        const response = await api.get<PaginatedResponse<INote>>(`/interviews/${interviewId}/alignment-notes`, {
          ...params,
          signal,
        });

        return response.data;
      },
      staleTime: 60 * 1000, // 1min
    });
  },
  roundTypesList: (params: Params) => {
    return queryOptions({
      queryKey: INTERVIEWS_KEYS.roundTypes(params),
      queryFn: async ({ signal }) => {
        const response = await api.get<PaginatedResponse<InterviewRoundType>>(`/interview-round-types`, {
          ...params,
          signal,
        });

        return response.data;
      },
    });
  },
  infiniteRoundTypesList: (params: Record<string, unknown>) => {
    const paginationConfig = createInfinitePaginationConfig(
      {
        orderBy: "rank",
        orderDir: "asc",
        published: true,
        ...params,
      },
      20,
    );

    return infiniteQueryOptions({
      queryKey: INTERVIEWS_KEYS.roundTypes({
        params: paginationConfig.keyParams,
      }),
      queryFn: async ({ pageParam = 1, signal }) => {
        const response = await api.get<PaginatedResponse<InterviewRoundType>>(`/interview-round-types`, {
          params: paginationConfig.queryFnParams(pageParam),
          signal,
        });

        return response.data;
      },
      ...paginationConfig.opts,
    });
  },
  closingNotesList: (interviewId: string | null) => {
    return queryOptions({
      queryKey: INTERVIEWS_KEYS.closingNotes(interviewId),
      queryFn: async ({ signal }) => {
        if (interviewId == null) {
          return null;
        }

        const response = await api.get<PaginatedResponse<INote>>(`/interviews/${interviewId}/closing-notes`, {
          params: {
            skip: 0,
            limit: 100,
          },
          signal,
        });

        return response.data;
      },
    });
  },
  listGlobalInterviewPhases: () => {
    return queryOptions({
      queryKey: INTERVIEWS_KEYS.listGlobalInterviewPhases(),
      queryFn: async ({ signal }) => {
        const { data } = await client.GET("/global-interview-phases/", {
          signal,
        });

        return ensureApiData(data, "GET /global-interview-phases/");
      },
    });
  },
  interviewRoundByTypeId: (interviewRoundTypeId: string) => {
    return queryOptions({
      queryKey: INTERVIEWS_KEYS.interviewRoundTypeById(interviewRoundTypeId),
      queryFn: async () => {
        const { data } = await client.GET("/interview-round-types/{interviewRoundTypeId}", {
          params: {
            path: {
              interviewRoundTypeId,
            },
          },
        });
        return ensureApiData(data, "GET /interview-round-types/{interviewRoundTypeId}");
      },
    });
  },
  member: {
    detail: (interviewId: string) => {
      return queryOptions({
        queryKey: INTERVIEWS_KEYS.member.detail(interviewId),
        queryFn: async ({ signal }) => {
          const response = await api.get(`/interviews/${interviewId}`, {
            signal,
          });

          return response.data;
        },
      });
    },
  },
};

export function useInfiniteLatestStageFilter(params: Record<string, unknown>) {
  return useInfiniteQuery(INTERVIEWS_QUERIES.infiniteRoundTypesList(params));
}
